import { Component, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import {
  RawMaterialCategory,
  RawMaterialCategoryItem,
  RawMaterialItem,
  RawMaterialStock,
  RawMaterialWarehouse,
  StockAdjustmentContext,
} from '../../raw-material.model';
import { RawMaterialService } from '../../raw-material.service';
import { finalize, forkJoin, Subscription } from 'rxjs';
import { PaginatedResponse, TableColumn } from '../../../common/models/common.model';
import { CompanyFactoryService } from '../../../production-inventory/services/company-factory.service';
import { Company } from '../../../production-inventory/production-inventory.model';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-inventory',
  standalone: false,
  templateUrl: './inventory.component.html',
  styleUrl: './inventory.component.scss',
})
export class InventoryComponent implements OnInit, OnDestroy {
  @Input() wareHouses: RawMaterialWarehouse[] = [];

  @ViewChild('adjustmentModal') adjustmentModalRef!: TemplateRef<any>;

  protected rawMaterialStock: RawMaterialStock[] = [];
  protected selectedWarehouse: string | null = null;
  protected selectedRawMaterialCategory: string | null = null;
  protected selectedCompany: string | null = null;
  protected selectedRawMaterial: string | null = null;
  protected rawMaterials: RawMaterialItem[] = [];
  protected hasNextPage: boolean = false;
  protected hasPreviousPage: boolean = false;
  protected totalCount: number = 0;
  protected isInventoryDataLoading: boolean = false;
  protected isExporting: boolean = false;
  protected rawMaterialCategories: RawMaterialCategoryItem[] = [];
  protected companies: Company[] = [];
  protected currentPage: number = 1;
  protected searchTerm: string = '';

  private searchDebounceTimer: any;

  private static readonly BASE_COLUMNS: TableColumn<RawMaterialStock>[] = [
    { label: 'Category', field: 'raw_material_category_name' },
    { label: 'Code', field: 'raw_material_code' },
    {
      label: 'Name',
      field: 'raw_material_name',
      format: (val, row) => `${row.raw_material_category_short_code} – ${val}`,
    },
    { label: 'Quantity Available', field: 'units_available' },
    { label: 'Unit', field: 'unit_name' },
    { label: 'Warehouse', field: 'warehouse_name' },
    { label: 'Stock Status', field: 'is_low_stock' },
  ];

  protected inventoryColumns: TableColumn<RawMaterialStock>[] = [
    ...InventoryComponent.BASE_COLUMNS,
    { label: '', btn_type: 'adjust' },
  ];

  /** The stock row the adjustment modal was opened from. */
  protected adjustmentContext: StockAdjustmentContext | null = null;

  private modalRef: NgbModalRef | null = null;
  private rawMaterialSubscription!: Subscription;

  constructor(
    private _rawMaterialService: RawMaterialService,
    private companyFactoryService: CompanyFactoryService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.companyFactoryService.listCompanies().subscribe({
      next: companies => (this.companies = companies),
    });

    this.loadCategoriesAndInventory();
  }

  protected openAdjustmentModal(stock: RawMaterialStock): void {
    this.adjustmentContext = {
      raw_material: stock.raw_material,
      raw_material_name: stock.raw_material_name,
      raw_material_code: stock.raw_material_code,
      warehouse: stock.warehouse,
      warehouse_name: stock.warehouse_name,
      unit_short_name: stock.unit_short_name,
      units_available: stock.units_available,
    };

    this.modalRef = this.modalService.open(this.adjustmentModalRef, { size: 'lg', backdrop: 'static' });
  }

  /** Refetch rather than patching the row: the server owns the balance. */
  protected onAdjustmentSaved(): void {
    this.closeAdjustmentModal();
    this.getInventoryDetails();
  }

  protected closeAdjustmentModal(): void {
    this.modalRef?.dismiss();
    this.modalRef = null;
    this.adjustmentContext = null;
  }

  private loadCategoriesAndInventory(): void {
    this.isInventoryDataLoading = true;

    // Both lists come back unpaginated, so the filter dropdowns hold every option.
    forkJoin({
      categories: this._rawMaterialService.get_categories(),
      materials: this._rawMaterialService.list(),
    }).subscribe({
      next: ({ categories, materials }) => {
        this.rawMaterialCategories = categories ?? [];
        this.rawMaterials = materials ?? [];

        // After the filter options are loaded → fetch inventory
        this.getInventoryDetails();
      },
      error: err => {
        console.error('Error fetching filter options', err);
        this.isInventoryDataLoading = false;
      },
    });
  }

  /** Relation filters take the object's UUID; a name comes back as an empty 200. */
  private get currentFilters(): Record<string, any> {
    const params: Record<string, any> = {};

    if (this.selectedWarehouse) params['warehouse'] = this.selectedWarehouse;
    if (this.selectedRawMaterialCategory) params['category'] = this.selectedRawMaterialCategory;
    if (this.selectedRawMaterial) params['raw_material'] = this.selectedRawMaterial;
    if (this.selectedCompany) params['company'] = this.selectedCompany;
    if (this.searchTerm) params['search'] = this.searchTerm;

    return params;
  }

  protected get hasActiveFilters(): boolean {
    return !!(
      this.searchTerm.trim() ||
      this.selectedWarehouse ||
      this.selectedRawMaterialCategory ||
      this.selectedRawMaterial ||
      this.selectedCompany
    );
  }

  private getInventoryDetails(): void {
    this.isInventoryDataLoading = true;

    const params: Record<string, any> = {
      page: this.currentPage,
      ...this.currentFilters,
    };

    this.rawMaterialSubscription?.unsubscribe();
    this.rawMaterialSubscription = this._rawMaterialService
      .get_stock(params)
      .pipe(finalize(() => (this.isInventoryDataLoading = false)))
      .subscribe({
        next: (rawMaterialStockResponse: PaginatedResponse<RawMaterialStock>) => {
          this.rawMaterialStock = rawMaterialStockResponse.results;
          this.totalCount = rawMaterialStockResponse.count;
          this.hasNextPage = !!rawMaterialStockResponse.next;
          this.hasPreviousPage = !!rawMaterialStockResponse.previous;
        },
        error: err => console.error('Error fetching inventory details', err),
      });
  }

  protected onPageChange(page: number): void {
    this.currentPage = page;
    this.getInventoryDetails();
  }

  ngOnDestroy(): void {
    this.rawMaterialSubscription?.unsubscribe();
    this.modalRef?.dismiss();
  }

  protected onWareHouseSelected(warehouse: string): void {
    this.selectedWarehouse = warehouse;
    this.currentPage = 1;
    this.getInventoryDetails();
  }

  protected onRawMaterialCategorySelected($event: any): void {
    this.currentPage = 1;
    this.getInventoryDetails();
  }

  protected onFilterChange(): void {
    this.currentPage = 1;
    this.getInventoryDetails();
  }

  protected clearFilters(): void {
    this.searchTerm = '';
    this.selectedRawMaterial = null;
    this.selectedRawMaterialCategory = null;
    this.selectedCompany = null;
    this.selectedWarehouse = null;

    this.onFilterChange();
  }

  protected onSearchKeyUp(): void {
    clearTimeout(this.searchDebounceTimer);
    this.searchDebounceTimer = setTimeout(() => {
      this.currentPage = 1;
      this.getInventoryDetails();
    }, 500);
  }

  protected onCompanySelected(): void {
    this.currentPage = 1;
    this.getInventoryDetails();
  }

  protected exportInventory(): void {
    this.isExporting = true;

    this._rawMaterialService
      .export_stock(this.currentFilters)
      .pipe(finalize(() => (this.isExporting = false)))
      .subscribe({
        next: (blob: Blob) => {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'raw_material_inventory.xlsx';
          a.click();
          URL.revokeObjectURL(url);
        },
        error: err => console.error('Export failed', err),
      });
  }

  protected readonly HTMLSelectElement = HTMLSelectElement;
}
